import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Download, X, Mail, Phone, Briefcase, Calendar, FileText, ChevronRight, MapPin, UserCheck, GraduationCap, BarChart3, Filter } from "lucide-react";
import { useAdminAuth } from "./AdminAuthContext";
import { processAnalytics } from "./components/AnalyticsCalculators";
import { DepartmentExecutiveSummary, RoleExecutiveSummary, RoleFillProgress, RoleHiringFunnel } from "./components/DepartmentRoleAnalytics";

const STATUS_OPTIONS = ["new", "reviewed", "shortlisted", "interviewed", "selected", "rejected"];

const STATUS_COLORS = {
  new: "bg-blue-500/20 text-blue-300 border-blue-500/40",
  reviewed: "bg-purple-500/20 text-purple-300 border-purple-500/40",
  shortlisted: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  interviewed: "bg-cyan-500/20 text-cyan-300 border-cyan-500/40",
  selected: "bg-green-500/20 text-green-300 border-green-500/40",
  rejected: "bg-red-500/20 text-red-300 border-red-500/40",
};

// Helper: Calculate CTC Growth % using formula: ((Expected CTC - Current CTC) / Current CTC) * 100
const getCtcGrowthVal = (app) => {
  if (app.ctc_increase_percentage && app.ctc_increase_percentage !== '0%' && app.ctc_increase_percentage !== '0') {
    const num = parseFloat(String(app.ctc_increase_percentage).replace(/[^0-9.-]/g, ''));
    if (!isNaN(num)) return num;
  }
  const parseNum = (str) => {
    if (!str || str === 'Not Specified' || str === 'N/A') return 0;
    const clean = String(str).replace(/,/g, '').replace(/[^0-9.]/g, '').trim();
    const num = parseFloat(clean);
    if (isNaN(num)) return 0;
    if (num > 10000) return num / 100000;
    return num;
  };
  const c = parseNum(app.current_ctc);
  const e = parseNum(app.expected_ctc);
  if (c > 0 && e > 0) {
    return ((e - c) / c) * 100;
  }
  return 0;
};

const AdminApplications = () => {
  const applicationsRef = useRef(null);
  
  useEffect(() => {
    console.log("APPLICATIONS_USE_EFFECT");
    console.log("window.location.pathname:", window.location.pathname);
    console.log(
      document.querySelector('[data-testid="admin-applications"]')
    );
    console.log("HAS_DASHBOARD_HTML", document.body.innerHTML.includes("admin-dashboard"));
    console.log("HAS_APPLICATIONS_HTML", document.body.innerHTML.includes("admin-applications"));
  }, []);
  const { api } = useAdminAuth();
  const [searchParams] = useSearchParams();
  const [applications, setApplications] = useState([]);
  const [filterStatus, setFilterStatus] = useState("");
  const [quickFilter, setQuickFilter] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");
  const [statusUpdate, setStatusUpdate] = useState("");
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [selectedDeptIndex, setSelectedDeptIndex] = useState(0);
  const [selectedRoleKey, setSelectedRoleKey] = useState("ea-ceo");

  console.debug("[ADMIN APPLICATIONS RENDER]", {
    loading,
    count: applications.length,
    timestamp: new Date().toISOString()
  });

  const analytics = useMemo(() => {
    return processAnalytics(applications);
  }, [applications]);

  const loadApplications = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      const res = await api().get("/admin/applications", { params });
      const appsList = Array.isArray(res.data)
        ? res.data
        : res.data?.applications || [];
      setApplications(appsList);
    } catch (err) {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [api, filterStatus]);

  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  useEffect(() => {
    const fParam = searchParams.get("filter");
    const sParam = searchParams.get("search");
    const cParam = searchParams.get("candidateId");

    if (sParam) setSearch(sParam);
    if (fParam) {
      if (["today", "outliers", "cqi90", "immediate"].includes(fParam)) {
        setQuickFilter(fParam);
      } else if (["new", "reviewed", "shortlisted", "interviewed", "selected", "rejected"].includes(fParam)) {
        setFilterStatus(fParam);
      }
    }
    if (cParam && applications.length > 0) {
      const match = applications.find(app => String(app.id) === String(cParam));
      if (match) {
        setSelected(match);
        setNotes(match.notes || "");
        setStatusUpdate(match.status);
      }
    }
  }, [searchParams, applications]);

  const openDetail = (app) => {
    setSelected(app);
    setNotes(app.notes || "");
    setStatusUpdate(app.status);
  };

  const handleUpdate = async () => {
    if (!selected) return;
    setUpdating(true);
    try {
      const res = await api().put(`/admin/applications/${selected.id}`, {
        status: statusUpdate,
        notes: notes,
      });
      setSelected(res.data);
      await loadApplications();
    } catch (err) {
      // ignore
    } finally {
      setUpdating(false);
    }
  };

  const handleDownloadResume = async (app) => {
    try {
      const res = await api().get(`/admin/applications/${app.id}/resume`);
      const url = res.data.url;
      if (url) {
        // If it's a local file path, build URL via /uploads mount
        if (url.startsWith("/app/backend/uploads/") || !url.startsWith("http")) {
          const filename = res.data.filename || url.split("/").pop();
          const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
          window.open(`${backendUrl}/uploads/${filename}`, "_blank");
        } else {
          window.open(url, "_blank");
        }
      }
    } catch (err) {
      // ignore
    }
  };

  const filtered = applications.filter((a) => {
    if (quickFilter === "today") {
      const created = new Date(a.created_at);
      const today = new Date();
      if (created.toDateString() !== today.toDateString()) return false;
    } else if (quickFilter === "outliers") {
      if (getCtcGrowthVal(a) < 50) return false;
    } else if (quickFilter === "cqi90") {
      if ((a.computedCqi || a.cqi_score || 85) < 88) return false;
    } else if (quickFilter === "immediate") {
      const np = String(a.notice_period || '').toLowerCase();
      if (!np.includes("immediate") && !np.includes("0") && !np.includes("15")) return false;
    }

    if (search) {
      const q = search.toLowerCase();
      const match = (
        (a.full_name || "").toLowerCase().includes(q) ||
        (a.email || "").toLowerCase().includes(q) ||
        (a.role_applied || a.position_title || "").toLowerCase().includes(q) ||
        (a.department || "").toLowerCase().includes(q) ||
        (a.application_id || a.reference_number || "").toLowerCase().includes(q)
      );
      if (!match) return false;
    }
    return true;
  });

  return (
    <div data-testid="admin-applications" ref={applicationsRef}>
      <div className="mb-8 flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-light text-white">Applications & Recruitment Workspace</h1>
          <p className="text-sm text-cyan-300/60 mt-1">Review candidate applications, position fill progress & hiring funnels</p>
        </div>
        <button
          onClick={() => setShowAnalytics(!showAnalytics)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-cyan-400/30 bg-cyan-400/10 hover:bg-cyan-400/20 text-cyan-300 text-xs font-semibold uppercase tracking-wider transition-all cursor-pointer"
          data-testid="toggle-analytics-btn"
        >
          <BarChart3 className="w-4 h-4" />
          <span>{showAnalytics ? "Hide Role & Dept Analytics" : "View Department & Role Analytics"}</span>
        </button>
      </div>

      {/* DEPARTMENT & ROLE EXECUTIVE ANALYTICS SECTION */}
      {showAnalytics && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          className="mb-8 space-y-6"
        >
          {/* Department Executive Summary Picker */}
          <div className="p-4 rounded-xl bg-slate-900/60 border border-white/10 flex items-center gap-3 overflow-x-auto">
            <span className="text-xs text-white/50 uppercase font-mono flex-shrink-0">Department:</span>
            {analytics.departmentSummaries.map((dept, idx) => (
              <button
                key={idx}
                onClick={() => setSelectedDeptIndex(idx)}
                className={`px-3 py-1.5 rounded-lg text-xs transition-all whitespace-nowrap cursor-pointer ${
                  selectedDeptIndex === idx
                    ? 'bg-cyan-400/20 text-cyan-300 border border-cyan-400/40 font-semibold'
                    : 'bg-white/5 text-white/60 hover:text-white border border-transparent'
                }`}
              >
                {dept.department}
              </button>
            ))}
          </div>

          <DepartmentExecutiveSummary 
            deptSummary={analytics.departmentSummaries[selectedDeptIndex]} 
          />

          {/* Role Executive Summary & Hiring Funnel Picker */}
          <div className="p-4 rounded-xl bg-slate-900/60 border border-white/10 flex items-center gap-3 overflow-x-auto">
            <span className="text-xs text-white/50 uppercase font-mono flex-shrink-0">Role:</span>
            {Object.keys(analytics.roleMetrics).map((roleKey) => {
              const r = analytics.roleMetrics[roleKey];
              return (
                <button
                  key={roleKey}
                  onClick={() => setSelectedRoleKey(roleKey)}
                  className={`px-3 py-1.5 rounded-lg text-xs transition-all whitespace-nowrap cursor-pointer ${
                    selectedRoleKey === roleKey
                      ? 'bg-emerald-400/20 text-emerald-300 border border-emerald-400/40 font-semibold'
                      : 'bg-white/5 text-white/60 hover:text-white border border-transparent'
                  }`}
                >
                  {r.title}
                </button>
              );
            })}
          </div>

          <RoleExecutiveSummary 
            roleMetric={analytics.roleMetrics[selectedRoleKey] || Object.values(analytics.roleMetrics)[0]} 
          />
        </motion.div>
      )}

      {/* Quick Filter Badges */}
      {quickFilter && (
        <div className="mb-4 p-2.5 rounded-xl bg-cyan-950/40 border border-cyan-500/30 flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs font-mono text-cyan-300">
            <Filter className="w-4 h-4 text-cyan-400" />
            <span>Active Quick Filter: <strong className="uppercase">{quickFilter}</strong> ({filtered.length} matches)</span>
          </div>
          <button
            onClick={() => setQuickFilter("")}
            className="text-xs text-white/50 hover:text-white underline font-mono cursor-pointer"
          >
            Clear Quick Filter
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <input
            type="text"
            placeholder="Search by name, email, role..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-black/40 border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-cyan-400/50"
            data-testid="applications-search-input"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-3 bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none focus:border-cyan-400/50"
          data-testid="applications-status-filter"
        >
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-white/10 overflow-hidden" style={{ background: "rgba(10,22,40,0.5)", backdropFilter: "blur(12px)" }}>
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/10 text-left">
              <th className="px-6 py-4 text-xs uppercase tracking-wider text-white/40">Candidate</th>
              <th className="px-6 py-4 text-xs uppercase tracking-wider text-white/40">Role</th>
              <th className="px-6 py-4 text-xs uppercase tracking-wider text-white/40">Experience</th>
              <th className="px-6 py-4 text-xs uppercase tracking-wider text-white/40">CTC & Growth %</th>
              <th className="px-6 py-4 text-xs uppercase tracking-wider text-white/40">Status</th>
              <th className="px-6 py-4 text-xs uppercase tracking-wider text-white/40">Applied</th>
              <th className="px-6 py-4 text-xs uppercase tracking-wider text-white/40"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="px-6 py-12 text-center text-white/40">Loading...</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={7} className="px-6 py-12 text-center text-white/40">No applications found</td></tr>
            )}
            {!loading && filtered.map((app) => {
              const growthVal = getCtcGrowthVal(app);
              const isOutlier = growthVal > 100;
              return (
                <tr
                  key={app.id}
                  onClick={() => openDetail(app)}
                  className="border-b border-white/5 hover:bg-cyan-400/5 cursor-pointer transition-colors"
                  data-testid={`application-row-${app.id}`}
                >
                  <td className="px-6 py-4">
                    <div className="text-white text-sm">{app.full_name}</div>
                    <div className="text-white/50 text-xs">{app.email}</div>
                  </td>
                  <td className="px-6 py-4 text-white/80 text-sm">{app.role_applied}</td>
                  <td className="px-6 py-4 text-white/60 text-sm">{app.experience_level}</td>
                  <td className="px-6 py-4 text-xs font-mono">
                    <div className="text-white">{app.current_ctc || 'N/A'} &rarr; {app.expected_ctc || 'N/A'}</div>
                    <div className={`mt-0.5 font-bold ${isOutlier ? 'text-red-400' : 'text-cyan-300'}`}>
                      +{growthVal.toFixed(1)}% CTC Growth {isOutlier ? '(Outlier)' : ''}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs border ${STATUS_COLORS[app.status] || "bg-white/10 text-white/70 border-white/20"}`}>
                      {app.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-white/60 text-xs">
                    {app.created_at ? new Date(app.created_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-6 py-4">
                    <ChevronRight className="w-4 h-4 text-white/30" />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Detail Drawer */}
      <AnimatePresence>
        {selected && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelected(null)}
              className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40"
            />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25 }}
              className="fixed top-0 right-0 h-full w-full max-w-xl z-50 overflow-y-auto"
              style={{ background: "linear-gradient(180deg, #0a1628 0%, #050a14 100%)", borderLeft: "1px solid rgba(255,255,255,0.1)" }}
              data-testid="application-detail-drawer"
            >
              <div className="sticky top-0 z-10 flex items-center justify-between p-6 border-b border-white/10" style={{ background: "rgba(10,22,40,0.95)", backdropFilter: "blur(20px)" }}>
                <h2 className="text-xl font-light text-white">Application Details</h2>
                <button onClick={() => setSelected(null)} className="text-white/60 hover:text-white" data-testid="close-detail-button">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Candidate Info */}
                <div className="space-y-3">
                  <div className="text-xs uppercase tracking-wider text-cyan-300/60">Candidate</div>
                  <div className="text-2xl font-light text-white">{selected.full_name}</div>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-white/80"><Mail className="w-4 h-4 text-cyan-300/60" />{selected.email}</div>
                    <div className="flex items-center gap-2 text-white/80"><Phone className="w-4 h-4 text-cyan-300/60" />{selected.phone}</div>
                    <div className="flex items-center gap-2 text-white/80" data-testid="admin-candidate-city">
                      <MapPin className="w-4 h-4 text-cyan-300/60" />
                      <span>{selected.city || 'N/A'}</span>
                      {selected.work_location_gurugram && (
                        <span className="text-xs px-2 py-0.5 rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/30">
                          Gurugram Office: {selected.work_location_gurugram}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-white/80"><Briefcase className="w-4 h-4 text-cyan-300/60" />{selected.role_applied}</div>
                    <div className="flex items-center gap-2 text-white/80"><Calendar className="w-4 h-4 text-cyan-300/60" />{selected.experience_level}</div>
                    {selected.employment_status && (
                      <div className="flex items-center gap-2 text-white/80" data-testid="admin-candidate-employment-status">
                        <UserCheck className="w-4 h-4 text-cyan-300/60" />
                        <span>Employment Status: {selected.employment_status}</span>
                      </div>
                    )}
                    {selected.highest_qualification && (
                      <div className="flex items-center gap-2 text-white/80" data-testid="admin-candidate-qualification">
                        <GraduationCap className="w-4 h-4 text-cyan-300/60" />
                        <span>{selected.highest_qualification}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Compensation Profile */}
                {(() => {
                  const growthVal = getCtcGrowthVal(selected);
                  const isOutlier = growthVal > 100;
                  return (
                    <div className="space-y-3 p-4 rounded-xl border border-cyan-500/20" style={{ background: "rgba(0,0,0,0.3)" }} data-testid="admin-compensation-profile">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="text-xs uppercase tracking-wider text-cyan-300 font-semibold">Compensation Profile</div>
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-mono font-bold border ${isOutlier ? 'bg-red-500/20 text-red-300 border-red-500/40' : 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40'}`}>
                          CTC Growth: +{growthVal.toFixed(1)}% {isOutlier ? '(Outlier >100%)' : ''}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <div className="text-white/40 text-xs">Current Annual CTC</div>
                          <div className="text-white font-medium mt-0.5" data-testid="admin-current-ctc">
                            {selected.current_ctc ? (isNaN(selected.current_ctc) ? selected.current_ctc : `INR ${Number(selected.current_ctc).toLocaleString('en-IN')} / Annum`) : 'Not Specified'}
                          </div>
                        </div>
                        <div>
                          <div className="text-white/40 text-xs">Expected Annual CTC</div>
                          <div className="text-white font-medium mt-0.5" data-testid="admin-expected-ctc">
                            {selected.expected_ctc ? (isNaN(selected.expected_ctc) ? selected.expected_ctc : `INR ${Number(selected.expected_ctc).toLocaleString('en-IN')} / Annum`) : 'Not Specified'}
                          </div>
                        </div>
                      </div>

                      <div className="pt-2 border-t border-white/10 flex items-center justify-between">
                        <div>
                          <div className="text-white/40 text-xs">Calculated CTC Growth %</div>
                          <div className={`font-bold text-sm mt-0.5 font-mono ${isOutlier ? 'text-red-400' : 'text-cyan-300'}`} data-testid="admin-ctc-increase">
                            +{growthVal.toFixed(1)}%
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-white/40 text-xs">Formula</div>
                          <div className="text-slate-400 text-[11px] font-mono">((Expected - Current) / Current) * 100</div>
                        </div>
                      </div>

                      {selected.compensation_justification && selected.compensation_justification !== 'N/A' && (
                        <div className="pt-2 border-t border-white/10">
                          <div className="text-white/40 text-xs mb-1">Compensation Justification</div>
                          <div className="p-3 rounded-lg bg-black/40 border border-white/10 text-xs text-white/90 whitespace-pre-wrap leading-relaxed" data-testid="admin-compensation-justification">
                            {selected.compensation_justification}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Cover Note */}
                {selected.cover_note && (
                  <div className="space-y-2">
                    <div className="text-xs uppercase tracking-wider text-cyan-300/60">Cover Note</div>
                    <div className="p-4 rounded-lg bg-black/40 border border-white/10 text-sm text-white/80 whitespace-pre-wrap">
                      {selected.cover_note}
                    </div>
                  </div>
                )}

                {/* Resume */}
                <div>
                  <button
                    onClick={() => handleDownloadResume(selected)}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-lg border border-cyan-400/30 bg-cyan-400/10 text-cyan-300 hover:bg-cyan-400/20 transition-all"
                    data-testid="download-resume-button"
                  >
                    <FileText className="w-4 h-4" />
                    <span className="text-sm">Download Resume</span>
                    <Download className="w-4 h-4" />
                  </button>
                </div>

                {/* Status Update */}
                <div className="space-y-3 pt-4 border-t border-white/10">
                  <div className="text-xs uppercase tracking-wider text-cyan-300/60">Update Status</div>
                  <select
                    value={statusUpdate}
                    onChange={(e) => setStatusUpdate(e.target.value)}
                    className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-lg text-white"
                    data-testid="update-status-select"
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>

                  <div className="text-xs uppercase tracking-wider text-cyan-300/60">Notes</div>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={4}
                    placeholder="Internal notes..."
                    className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-lg text-white placeholder-white/30"
                    data-testid="update-notes-input"
                  />

                  <button
                    onClick={handleUpdate}
                    disabled={updating}
                    className="w-full py-3 rounded-lg font-medium uppercase tracking-wider text-sm disabled:opacity-60"
                    style={{ background: "linear-gradient(135deg, #00FFD1 0%, #00a884 100%)", color: "#000" }}
                    data-testid="update-application-button"
                  >
                    {updating ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminApplications;
